import { GMW, UploadTask } from "@nens/brostar/openapi-client";
import type { MonitoringTube as ApiMonitoringTube } from "@nens/brostar/openapi-client";
import {
  CreateGMWTask,
  EditGMWTask,
  constructionStandardValues,
  groundLevelPositioningMethodStandardValues,
  indicationYesNoOptionsType,
  initialFunctionStandardValues,
  positioningMethodStandardValues,
  qualityRegimeOptionsType,
  verticalDatumStandardValues,
  wellHeadProtectorStandardValues,
  wellStabilityStandardValues,
} from "@nens/brostar/uploadapi-client";
import { Electrode, GeoOhmCable, MonitoringTube } from "@nens/brostar/uploadapi-client";

import { isValidEnum } from "./is-valid-enum";

// Raw shapes from the openapi-client SDK before they are mapped to uploadapi-client types.
// The SDK converts JSON snake_case to camelCase for top-level MonitoringTube fields, but
// geoOhmCables is typed as `object` and retains the original snake_case key names.
interface RawElectrode {
  electrode_number: string;
  electrode_packing_material: string;
  electrode_position: string | number;
  electrode_status: string;
}

interface RawGeoOhmCable {
  cable_number: string;
  electrodes: RawElectrode[];
}

const convertElectrode = (raw: RawElectrode): Electrode => ({
  electrodeNumber: raw.electrode_number,
  electrodePackingMaterial: raw.electrode_packing_material as Electrode["electrodePackingMaterial"],
  electrodePosition: +raw.electrode_position,
  electrodeStatus: raw.electrode_status as Electrode["electrodeStatus"],
});

const convertGeoOhmCable = (raw: RawGeoOhmCable): GeoOhmCable => ({
  cableNumber: raw.cable_number,
  electrodes: Array.isArray(raw.electrodes) ? raw.electrodes.map(convertElectrode) : [],
});

const convertMonitoringTube = (raw: ApiMonitoringTube): MonitoringTube => {
  const rawCables = Array.isArray(raw.geoOhmCables) ? (raw.geoOhmCables as RawGeoOhmCable[]) : [];

  return {
    artesianWellCapPresent: raw.artesianWellCapPresent as MonitoringTube["artesianWellCapPresent"],
    sedimentSumpPresent: raw.sedimentSumpPresent as MonitoringTube["sedimentSumpPresent"],
    variableDiameter: raw.variableDiameter as MonitoringTube["variableDiameter"],
    tubePackingMaterial: raw.tubePackingMaterial as MonitoringTube["tubePackingMaterial"],
    tubeStatus: raw.tubeStatus as MonitoringTube["tubeStatus"],
    tubeType: raw.tubeType as MonitoringTube["tubeType"],
    tubeTopPositioningMethod:
      raw.tubeTopPositioningMethod as MonitoringTube["tubeTopPositioningMethod"],
    tubeMaterial: raw.tubeMaterial as MonitoringTube["tubeMaterial"],
    glue: raw.glue as MonitoringTube["glue"],
    sockMaterial: raw.sockMaterial as MonitoringTube["sockMaterial"],
    tubeNumber: raw.tubeNumber + "",
    numberOfGeoOhmCables: raw.numberOfGeoOhmCables ? +raw.numberOfGeoOhmCables : 0,
    tubeTopDiameter: raw.tubeTopDiameter ? +raw.tubeTopDiameter : NaN,
    tubeTopPosition: raw.tubeTopPosition ? +raw.tubeTopPosition : 0,
    screenLength: raw.screenLength ? +raw.screenLength : 1,
    plainTubePartLength: raw.plainTubePartLength ? +raw.plainTubePartLength : 0.1,
    sedimentSumpLength: raw.sedimentSumpLength ? +raw.sedimentSumpLength : NaN,
    screenProtection: raw.screenProtection as MonitoringTube["screenProtection"],
    geoOhmCables: rawCables.map(convertGeoOhmCable),
  };
};

// EDIT
export const convertGMWToEditTask = (
  object: GMW,
  monitoringTubes: ApiMonitoringTube[]
): EditGMWTask => {
  if (object.wellStability === null || object.wellStability === undefined) {
    object.wellStability = "";
  }
  const {
    deliveryContext,
    constructionStandard,
    initialFunction,
    nitgCode,
    groundLevelStable,
    owner,
    wellHeadProtector,
    wellConstructionDate,
    deliveredLocation,
    horizontalPositioningMethod,
    offset,
    verticalDatum,
    groundLevelPosition,
    groundLevelPositioningMethod,
    wellStability,
    deliveryAccountableParty,
    qualityRegime,
    broId,
  } = object;
  return {
    sourcedocumentData: {
      objectIdAccountableParty: "",
      nitgCode: nitgCode ?? "",
      maintenanceResponsibleParty: "",
      numberOfMonitoringTubes: monitoringTubes.length,
      monitoringTubes: monitoringTubes.map(convertMonitoringTube),
      deliveryContext: deliveryContext ?? "",
      constructionStandard: isValidEnum(constructionStandardValues, constructionStandard),
      initialFunction: isValidEnum(initialFunctionStandardValues, initialFunction),
      groundLevelStable: isValidEnum(indicationYesNoOptionsType, groundLevelStable),
      owner: owner ?? "",
      wellHeadProtector: isValidEnum(wellHeadProtectorStandardValues, wellHeadProtector),
      wellConstructionDate: wellConstructionDate ?? "",
      deliveredLocation: deliveredLocation ?? "",
      horizontalPositioningMethod: isValidEnum(
        positioningMethodStandardValues,
        horizontalPositioningMethod
      ),
      localVerticalReferencePoint: "NAP",
      offset: offset ?? "",
      verticalDatum: isValidEnum(verticalDatumStandardValues, verticalDatum),
      groundLevelPosition: groundLevelPosition ?? "",
      groundLevelPositioningMethod: isValidEnum(
        groundLevelPositioningMethodStandardValues,
        groundLevelPositioningMethod
      ),
      // if wellStability is null or undefined, set it to empty string to avoid validation errors, as the field is required in the API but may be missing in the form data
      wellStability: wellStability ? isValidEnum(wellStabilityStandardValues, wellStability) : "",
      isAbroad: "",
      geometricDataPubliclyAvailable: "",
      relatedSurveys: [],
    },

    metadata: {
      deliveryAccountableParty: deliveryAccountableParty ?? "",
      qualityRegime: isValidEnum(qualityRegimeOptionsType, qualityRegime),
      projectNumber: "",
      correctionReason: "eigenCorrectie",
      broId,
    },
  };
};

// UPLOAD TASK
export const convertGMWUploadTaskToCreateTask = ({
  projectNumber,
  metadata,
  sourcedocumentData,
}: UploadTask): CreateGMWTask => {
  const { qualityRegime, deliveryAccountableParty } = metadata as { [key: string]: string };
  const {
    deliveryContext,
    constructionStandard,
    initialFunction,
    groundLevelStable,
    owner,
    wellHeadProtector,
    wellConstructionDate,
    deliveredLocation,
    horizontalPositioningMethod,
    verticalDatum,
    nitgCode,
    offset,
    groundLevelPosition,
    groundLevelPositioningMethod,
    wellStability,
  } = sourcedocumentData as { [key: string]: string };
  const monitoringTubes =
    (
      sourcedocumentData as {
        monitoringTubes: CreateGMWTask["sourcedocumentData"]["monitoringTubes"];
      }
    )?.monitoringTubes ?? [];

  return {
    sourcedocumentData: {
      objectIdAccountableParty: "",
      nitgCode: nitgCode ?? "",
      maintenanceResponsibleParty: "",
      numberOfMonitoringTubes: monitoringTubes.length ?? 0,
      monitoringTubes: monitoringTubes,
      deliveryContext: deliveryContext ?? "",
      constructionStandard: isValidEnum(constructionStandardValues, constructionStandard),
      initialFunction: isValidEnum(initialFunctionStandardValues, initialFunction),
      groundLevelStable: isValidEnum(indicationYesNoOptionsType, groundLevelStable),
      owner: owner ?? "",
      wellHeadProtector: isValidEnum(wellHeadProtectorStandardValues, wellHeadProtector),
      wellConstructionDate: wellConstructionDate ?? "",
      deliveredLocation: deliveredLocation ?? "",
      horizontalPositioningMethod: isValidEnum(
        positioningMethodStandardValues,
        horizontalPositioningMethod
      ),
      localVerticalReferencePoint: "NAP",
      offset: offset ?? "",
      verticalDatum: isValidEnum(verticalDatumStandardValues, verticalDatum),
      groundLevelPosition: groundLevelPosition ?? "",
      groundLevelPositioningMethod: isValidEnum(
        groundLevelPositioningMethodStandardValues,
        groundLevelPositioningMethod
      ),
      // if wellStability is null or undefined, set it to empty string to avoid validation errors, as the field is required in the API but may be missing in the form data
      wellStability: wellStability ? isValidEnum(wellStabilityStandardValues, wellStability) : "",
      isAbroad: "",
      geometricDataPubliclyAvailable: "",
      relatedSurveys: [],
    },

    metadata: {
      deliveryAccountableParty: deliveryAccountableParty,
      qualityRegime: isValidEnum(qualityRegimeOptionsType, qualityRegime),
      projectNumber,
    },
  };
};
